import { SlidersHorizontal, RotateCcw, Search } from "lucide-react";
import { DateInput } from "@/components/ui/DateInput";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/cn";
import type { FilterBarProps, FilterValues } from "./types";

function normalizeSupplierFilter(v: FilterValues["supplierFilter"] | unknown): string[] {
  if (Array.isArray(v)) return v.map(String).filter((s) => s.trim() !== "");
  if (typeof v === "string" && v.trim()) return [v.trim()];
  return [];
}

function normalizeItemFilter(v: FilterValues["itemFilter"] | unknown): string[] {
  if (Array.isArray(v)) return v.map(String).filter((s) => s.trim() !== "");
  if (typeof v === "string" && v.trim()) return [v.trim()];
  return [];
}

function FilterTextField({
  label,
  value,
  onChange,
  placeholder,
  fieldClassName,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Largura do bloco (ex.: uma linha só no Kanban). */
  fieldClassName?: string;
  disabled?: boolean;
}) {
  const id = `flt-${label.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "")}`;
  return (
    <div className={cn("flex flex-col gap-1.5", fieldClassName ?? "min-w-[92px] max-w-[220px] flex-1")}>
      <label htmlFor={id} className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        disabled={disabled}
        className={cn(
          "h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-2 text-xs text-slate-800 sm:text-sm",
          "placeholder:text-slate-400 transition-colors",
          "focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600",
          disabled && "cursor-not-allowed opacity-60"
        )}
      />
    </div>
  );
}

export function FilterBar({
  values,
  onChange,
  onApply,
  onReset,
  costCenterOptions = [],
  constructiveUnitOptions,
  loadingOptions = false,
  loadingConstructiveUnitOptions = false,
  loading = false,
  className,
  headerEnd,
  showCostCenterOnly = false,
  showSuprimentosEntityFilters = false,
  showSuprimentosKanbanDocFilters = false,
  supplierOptions = [],
  loadingSupplierOptions = false,
  itemOptions = [],
  loadingItemOptions = false,
  showUnitSearch = false,
  unitSearch = "",
  onUnitSearchChange,
  unitSearchDisabled = false,
  unitSearchLabel = "Buscar unidade",
  filterActions,
}: FilterBarProps) {
  const safeValues: FilterValues = {
    startDate: values.startDate ?? "",
    endDate: values.endDate ?? "",
    costCenter: Array.isArray(values.costCenter) ? values.costCenter : [],
    constructiveUnit: Array.isArray(values.constructiveUnit) ? values.constructiveUnit : [],
    itemFilter: normalizeItemFilter(values.itemFilter),
    quotationFilter: values.quotationFilter ?? "",
    purchaseRequestFilter: values.purchaseRequestFilter ?? "",
    orderFilter: values.orderFilter ?? "",
    supplierFilter: normalizeSupplierFilter(values.supplierFilter),
  };

  const qf = safeValues.quotationFilter;
  const prf = safeValues.purchaseRequestFilter;
  const orf = safeValues.orderFilter;

  const setField = (field: keyof FilterValues) => (val: string) => onChange({ ...safeValues, [field]: val });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") onApply();
  };

  return (
    <div
      className={cn(
        "bg-white rounded-xl border border-slate-200 shadow-card px-5 py-4",
        className
      )}
      onKeyDown={handleKeyDown}
    >
      {/* Cabeçalho da barra */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <SlidersHorizontal className="h-3.5 w-3.5 flex-shrink-0 text-brand-600" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Filtros</span>
        </div>
        {headerEnd ? <div className="flex flex-shrink-0 items-center gap-2">{headerEnd}</div> : null}
      </div>

      {showCostCenterOnly ? (
        <div className="flex flex-wrap items-end gap-2 sm:gap-3">
          <div className="min-w-[220px] flex-1 sm:max-w-xl">
            <MultiSelect
              label="Centro de Custo / Obra"
              value={safeValues.costCenter}
              onChange={(vals) => onChange({ ...safeValues, costCenter: vals })}
              options={costCenterOptions}
              placeholder="Todos"
              loading={loadingOptions}
            />
          </div>
          {constructiveUnitOptions != null ? (
            <div className="min-w-[200px] flex-1 sm:max-w-xl">
              <MultiSelect
                label="Unidade construtiva"
                value={safeValues.constructiveUnit}
                onChange={(vals) => onChange({ ...safeValues, constructiveUnit: vals })}
                options={constructiveUnitOptions}
                placeholder="Todas"
                loading={loadingConstructiveUnitOptions}
              />
            </div>
          ) : null}
          {showUnitSearch && onUnitSearchChange ? (
            <FilterTextField
              label={unitSearchLabel}
              value={unitSearch}
              onChange={onUnitSearchChange}
              placeholder="Nome, número da casa, ID…"
              fieldClassName="min-w-[160px] flex-1 sm:max-w-xs"
              disabled={unitSearchDisabled}
            />
          ) : null}
          {filterActions ? (
            <div className="flex flex-shrink-0 items-end gap-2 self-end">{filterActions}</div>
          ) : null}
          <div className="ml-auto flex flex-shrink-0 items-end gap-2 self-end">
            <Button variant="primary" size="md" onClick={onApply} loading={loading} leftIcon={<Search className="h-4 w-4" />}>
              Aplicar
            </Button>
            {onReset && (
              <Button variant="ghost" size="md" onClick={onReset} title="Limpar filtros">
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ) : showSuprimentosEntityFilters ? (
        <div className="flex min-w-0 flex-nowrap items-end gap-2 sm:gap-2.5">
          <div className="flex min-w-0 flex-1 flex-nowrap items-end gap-2 overflow-x-auto overflow-y-visible pb-0.5 sm:gap-2.5 [scrollbar-gutter:stable]">
            <div className="w-[118px] flex-shrink-0 sm:w-[128px]">
              <DateInput
                label="Data Inicial"
                value={safeValues.startDate}
                onChange={setField("startDate")}
                max={safeValues.endDate || undefined}
              />
            </div>
            <div className="w-[118px] flex-shrink-0 sm:w-[128px]">
              <DateInput
                label="Data Final"
                value={safeValues.endDate}
                onChange={setField("endDate")}
                min={safeValues.startDate || undefined}
              />
            </div>
            <div className="mb-0.5 hidden h-10 w-px flex-shrink-0 self-end bg-slate-200 sm:block" />
            <div className="min-w-[120px] max-w-[160px] flex-shrink-0 sm:min-w-[140px] sm:max-w-[200px] lg:max-w-[240px]">
              <MultiSelect
                label="Centro de Custo / Obra"
                value={safeValues.costCenter}
                onChange={(vals) => onChange({ ...safeValues, costCenter: vals })}
                options={costCenterOptions}
                placeholder="Todos"
                loading={loadingOptions}
                dropdownMinWidth={440}
              />
            </div>
            <div className="mb-0.5 hidden h-10 w-px flex-shrink-0 self-end bg-slate-200 md:block" />
            {showSuprimentosKanbanDocFilters ? (
              <>
                <FilterTextField
                  label="Cotação"
                  value={qf}
                  onChange={setField("quotationFilter")}
                  placeholder="Nº"
                  fieldClassName="w-[76px] flex-shrink-0 sm:w-[84px]"
                />
                <FilterTextField
                  label="Solicitação"
                  value={prf}
                  onChange={setField("purchaseRequestFilter")}
                  placeholder="SC"
                  fieldClassName="w-[84px] flex-shrink-0 sm:w-[92px]"
                />
                <FilterTextField
                  label="Pedido"
                  value={orf}
                  onChange={setField("orderFilter")}
                  placeholder="Nº"
                  fieldClassName="w-[76px] flex-shrink-0 sm:w-[84px]"
                />
                <div className="mb-0.5 hidden h-10 w-px flex-shrink-0 self-end bg-slate-200 lg:block" />
              </>
            ) : (
              <FilterTextField
                label="Nº Solicitação"
                value={prf}
                onChange={setField("purchaseRequestFilter")}
                placeholder="Ex.: 12345"
                fieldClassName="w-[112px] flex-shrink-0 sm:w-[128px]"
              />
            )}
            <div className="min-w-[160px] max-w-[220px] flex-shrink-0 sm:min-w-[200px] sm:max-w-[280px] lg:min-w-[220px] lg:max-w-[340px]">
              <MultiSelect
                label="Item"
                value={safeValues.itemFilter}
                onChange={(vals) => onChange({ ...safeValues, itemFilter: vals })}
                options={itemOptions}
                placeholder="Todos"
                loading={loadingItemOptions}
                dropdownMinWidth={440}
              />
            </div>
            <div className="min-w-[160px] max-w-[220px] flex-shrink-0 sm:min-w-[200px] sm:max-w-[280px] lg:min-w-[220px] lg:max-w-[340px]">
              <MultiSelect
                label="Fornecedor"
                value={safeValues.supplierFilter}
                onChange={(vals) => onChange({ ...safeValues, supplierFilter: vals })}
                options={supplierOptions}
                placeholder="Todos"
                loading={loadingSupplierOptions}
                dropdownMinWidth={440}
              />
            </div>
          </div>
          <div className="flex flex-shrink-0 items-end gap-2 border-l border-slate-200 bg-white pl-2 sm:pl-3">
            <Button variant="primary" size="md" onClick={onApply} loading={loading} leftIcon={<Search className="h-4 w-4" />}>
              Aplicar
            </Button>
            {onReset && (
              <Button variant="ghost" size="md" onClick={onReset} title="Limpar filtros">
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-2 sm:gap-3">
          <div className="min-w-[140px] max-w-[180px] flex-1">
            <DateInput
              label="Data Inicial"
              value={safeValues.startDate}
              onChange={setField("startDate")}
              max={safeValues.endDate || undefined}
            />
          </div>

          <div className="min-w-[140px] max-w-[180px] flex-1">
            <DateInput
              label="Data Final"
              value={safeValues.endDate}
              onChange={setField("endDate")}
              min={safeValues.startDate || undefined}
            />
          </div>

          <div className="mb-0.5 hidden h-10 w-px self-end bg-slate-200 sm:block" />

          <div className="min-w-[220px] flex-[2]">
            <MultiSelect
              label="Centro de Custo / Obra"
              value={safeValues.costCenter}
              onChange={(vals) => onChange({ ...safeValues, costCenter: vals })}
              options={costCenterOptions}
              placeholder="Todos"
              loading={loadingOptions}
            />
          </div>

          <div className="ml-auto flex flex-shrink-0 items-end gap-2 self-end">
            <Button variant="primary" size="md" onClick={onApply} loading={loading} leftIcon={<Search className="h-4 w-4" />}>
              Aplicar
            </Button>

            {onReset && (
              <Button variant="ghost" size="md" onClick={onReset} title="Limpar filtros">
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
